import type { PostTagCategory } from "./danbooru-tag-types"

/**
 * Tag types for sources whose post list carries none (the viewer then shows every tag as General).
 *
 * - Safebooru, Xbooru and TBIB (Gelbooru 0.2 API): one request per tag (`dapi s=tag name=...`, XML; the `names=` form is ignored).
 *   Each answer is remembered for the life of the server instance, a tag that has no entry is remembered as general, and at most
 *   MAX_NEW_LOOKUPS unknown tags are asked per call, so one post can never send dozens of requests to the site.
 * - Yande.re (Moebooru): its per-tag search matches pieces of names, so the exact tag is often missing from its answer. It publishes
 *   the whole tag list with types in one file instead (`/tag/summary.json`, about 2.3 MB, 130,000 tags); that is read once a day.
 *
 * Both checked live on 2026-10-25. Nothing here is a secret or a visitor's data, so the route that serves it can be cached.
 */
export const TAG_TYPE_SITES = ["safebooru", "xbooru", "tbib", "hypnohub", "yandere"] as const
export type TagTypeSite = (typeof TAG_TYPE_SITES)[number]

export function isTagTypeSite(value: unknown): value is TagTypeSite {
  return typeof value === "string" && (TAG_TYPE_SITES as readonly string[]).includes(value)
}

const DAY_MS = 24 * 60 * 60 * 1000
const MAX_NEW_LOOKUPS = 24 // the most NEW tags asked of a site in one call (sites that take parallel lookups raise it, below)

/** Gelbooru 0.2 sites that answer `dapi s=tag` (checked live 2026-10-25/26), and how many lookups each takes at once. Safebooru and Xbooru
 * turn away parallel requests (about half of 24 lookups failed at 6 at a time, none failed one at a time); TBIB took 6 at a time with no failure. */
const GELBOORU_SITES: Record<string, { host: string; concurrency: number; maxNew: number }> = {
  safebooru: { host: "https://safebooru.org", concurrency: 1, maxNew: MAX_NEW_LOOKUPS },
  xbooru: { host: "https://xbooru.com", concurrency: 1, maxNew: MAX_NEW_LOOKUPS },
  tbib: { host: "https://tbib.org", concurrency: 6, maxNew: 120 }, // 52 lookups took about 1 s at 6 at a time
  hypnohub: { host: "https://hypnohub.net", concurrency: 6, maxNew: 120 }, // 24 of 24 lookups at 6 at a time answered (2026-10-27)
}
const GELBOORU_CACHE_LIMIT = 30_000
const USER_AGENT = "Goonru/1.0 (tag types)"

export function categoryForGelbooruType(type: number): PostTagCategory {
  if (type === 1) return "artist"
  if (type === 3) return "copyright"
  if (type === 4) return "character"
  if (type === 5) return "meta"
  return "general"
}

/** Moebooru: 1 artist, 3 copyright, 4 character, 5 circle (a group of artists), 6 faults (a quality note). */
export function categoryForMoebooruType(type: number): PostTagCategory {
  if (type === 1 || type === 5) return "artist"
  if (type === 3) return "copyright"
  if (type === 4) return "character"
  if (type === 6) return "meta"
  return "general"
}

function decodeEntities(value: string): string {
  return value.replace(/&quot;/g, '"').replace(/&#0?39;/g, "'").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&")
}

/** The type of the named tag in a Gelbooru 0.2 tag answer (`<tag type="4" name="x" .../>`), or null when it is not listed. */
export function parseGelbooruTagType(xml: string, name: string): number | null {
  for (const match of xml.matchAll(/<tag\b[^>]*\btype="(\d+)"[^>]*\bname="([^"]*)"/g)) {
    if (decodeEntities(match[2]) === name) return Number(match[1])
  }
  return null
}

/** Moebooru's tag summary: rows separated by a space, each "type`name`alias`alias`". Aliases are not read. */
export function parseMoebooruSummary(data: string): Map<string, number> {
  const types = new Map<string, number>()
  for (const row of data.split(" ")) {
    const [type, name] = row.split("`")
    if (name && type !== "" && Number.isFinite(Number(type))) types.set(name, Number(type))
  }
  return types
}

type Fetcher = typeof fetch

const gelbooruTypes = new Map<string, PostTagCategory>() // keyed by `${site} ${tag}`
let yandereSummary: { at: number; types: Map<string, number> } | null = null
let yandereLoading: Promise<Map<string, number> | null> | null = null

async function loadYandereSummary(fetcher: Fetcher, now: number): Promise<Map<string, number> | null> {
  if (yandereSummary && now - yandereSummary.at < DAY_MS) return yandereSummary.types
  if (!yandereLoading) {
    yandereLoading = (async () => {
      try {
        const response = await fetcher("https://yande.re/tag/summary.json", {
          headers: { "User-Agent": "Mozilla/5.0", Accept: "application/json" },
          signal: AbortSignal.timeout(25_000),
        })
        if (!response.ok) return null
        const body = (await response.json()) as { data?: unknown }
        if (typeof body.data !== "string") return null
        const types = parseMoebooruSummary(body.data)
        if (types.size > 0) yandereSummary = { at: now, types }
        return types.size > 0 ? types : null
      } catch {
        return null
      } finally {
        yandereLoading = null
      }
    })()
  }
  return yandereLoading
}

async function lookupGelbooru(site: string, tags: string[], fetcher: Fetcher): Promise<void> {
  const { host, concurrency, maxNew } = GELBOORU_SITES[site]
  const unknown = tags.filter((tag) => !gelbooruTypes.has(`${site} ${tag}`)).slice(0, maxNew)
  let next = 0
  const worker = async () => {
    while (next < unknown.length) {
      const tag = unknown[next++]
      try {
        const response = await fetcher(
          `${host}/index.php?page=dapi&s=tag&q=index&name=${encodeURIComponent(tag)}`,
          { headers: { "User-Agent": USER_AGENT }, signal: AbortSignal.timeout(8_000) },
        )
        if (!response.ok) continue // a failed lookup is not remembered, so it is tried again later
        const type = parseGelbooruTagType(await response.text(), tag)
        if (gelbooruTypes.size >= GELBOORU_CACHE_LIMIT) gelbooruTypes.clear()
        gelbooruTypes.set(`${site} ${tag}`, type === null ? "general" : categoryForGelbooruType(type))
      } catch {
        // a slow or failed lookup leaves this tag as general for now
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, unknown.length) }, worker))
}

/** The type of each given tag. A tag that could not be looked up is left out (the viewer treats it as general). */
export async function lookupTagCategories(
  site: TagTypeSite,
  tags: string[],
  options: { fetcher?: Fetcher; now?: number } = {},
): Promise<Record<string, PostTagCategory>> {
  const fetcher = options.fetcher ?? fetch
  const out: Record<string, PostTagCategory> = {}
  if (site === "yandere") {
    const types = await loadYandereSummary(fetcher, options.now ?? Date.now())
    if (!types) return out
    for (const tag of tags) out[tag] = categoryForMoebooruType(types.get(tag) ?? 0)
    return out
  }
  await lookupGelbooru(site, tags, fetcher)
  for (const tag of tags) {
    const known = gelbooruTypes.get(`${site} ${tag}`)
    if (known) out[tag] = known
  }
  return out
}

/** For tests: forget everything remembered. */
export function resetTagTypeCaches(): void {
  gelbooruTypes.clear()
  yandereSummary = null
  yandereLoading = null
}
